import { useState, useEffect, ChangeEvent } from 'react';
import { CloseOutlined, SearchOutlined, CalculatorOutlined } from '@ant-design/icons';
import { Modal, Form, Title, Text, Tag } from '@bancobarigui/barihaus';
import { Input as AntInput, Empty, Radio } from 'antd';

import { RegisterFormButtons } from '@/components/RegisterFormButtons';
import { useModalForm } from '@/shared/hooks/useModalForm';
import { inspectionAssignmentService } from '@/services/inspectionAssignment.service';
import { providerCompanyService } from '@/services/providerCompany.service';
import { ProviderCompany } from '@/services/types/user.types';
import { useErrorHandler } from '@/shared/hooks/useErrorHandler';

interface AssignCalculationsModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  itemId: string;
  proposalNumber?: string;
  title?: string;
  description?: string;
  successMessage?: string;
  serviceMethod?: 'assignCalculations' | 'forwardAutomaticToProvider';
}

interface AssignCalculationsFormData {
  providerId?: string;
  notes?: string;
}

export function AssignCalculationsModal({
  open,
  onClose,
  onSuccess,
  itemId,
  proposalNumber,
  title,
  description,
  successMessage,
  serviceMethod = 'assignCalculations',
}: AssignCalculationsModalProps) {
  const [providers, setProviders] = useState<ProviderCompany[]>([]);
  const [filteredProviders, setFilteredProviders] = useState<ProviderCompany[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showOnlyQualified, setShowOnlyQualified] = useState(false);
  const [isLoadingProviders, setIsLoadingProviders] = useState(false);

  const { handleError } = useErrorHandler();

  const { form, isSubmitting, handleFormSubmit } = useModalForm<AssignCalculationsFormData>({
    onSubmit: async (values) => {
      if (!values.providerId) {
        throw new Error('Selecione uma prestadora');
      }
      await inspectionAssignmentService[serviceMethod](itemId, {
        providerId: values.providerId,
        notes: values.notes,
      });
    },
    onClose,
    onSuccess,
    successMessage: successMessage || 'Cálculos encaminhados para a prestadora com sucesso!',
    errorMessage: 'Erro ao encaminhar para prestadora',
  });

  useEffect(() => {
    if (open) {
      fetchProviders();
    } else {
      form.resetFields();
      setSearchTerm('');
      setShowOnlyQualified(false);
    }
  }, [open]);

  useEffect(() => {
    let filtered = [...providers];

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (provider) =>
          provider.name.toLowerCase().includes(term) ||
          provider.cnpj.toLowerCase().includes(term) ||
          provider.shortName?.toLowerCase().includes(term)
      );
    }

    if (showOnlyQualified) {
      filtered = filtered.filter((provider) => provider.allowsDemandReceiving);
    }

    setFilteredProviders(filtered);
  }, [searchTerm, showOnlyQualified, providers]);

  const fetchProviders = async () => {
    setIsLoadingProviders(true);
    try {
      const response = await providerCompanyService.getAllProviders();
      setProviders(response);
      setFilteredProviders(response);
    } catch (error) {
      handleError(error, 'Erro ao carregar prestadoras');
    } finally {
      setIsLoadingProviders(false);
    }
  };

  return (
    <Modal
      centered
      open={open}
      width={700}
      footer={null}
      onCancel={onClose}
      className="space-y-8"
      closeIcon={<CloseOutlined />}
    >
      <div className="mb-6">
        <Title level={2}>
          <CalculatorOutlined style={{ marginRight: 8 }} />
          {title || `ENCAMINHAR CÁLCULOS ${proposalNumber ? `- #${proposalNumber}` : ''}`}
        </Title>
        <Text className="block mt-2">
          {description || 'Selecione a prestadora que realizará os cálculos deste laudo. A prestadora receberá o laudo em modo somente leitura e poderá preencher apenas a metodologia/cálculos.'}
        </Text>
      </div>

      <Form form={form} layout="vertical" onFinish={handleFormSubmit} className="mt-4">
        <Form.Item name="notes" label="Observações">
          <AntInput.TextArea placeholder="Adicionar observações (opcional)" rows={3} maxLength={500} />
        </Form.Item>

        <div className="mb-4">
          <Text strong className="block mb-2">
            SELECIONAR PRESTADORA
          </Text>
          <AntInput
            placeholder="Pesquise por nome ou CNPJ"
            prefix={<SearchOutlined className="text-grey_500" />}
            value={searchTerm}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
            className="mb-4"
          />
        </div>

        <div className="flex items-center justify-between mb-4">
          <Text>Somente prestadoras aptas:</Text>
          <input
            type="checkbox"
            checked={showOnlyQualified}
            onChange={(e) => setShowOnlyQualified(e.target.checked)}
          />
        </div>

        <Form.Item
          name="providerId"
          rules={[{ required: true, message: 'Selecione uma prestadora' }]}
        >
          <Radio.Group className="w-full">
            <div className="flex flex-col border-2 border-dashed border-grey_200 rounded-lg p-6 min-h-[200px]">
              {isLoadingProviders ? (
                <div className="flex items-center justify-center flex-1">
                  <Text className="text-grey_500">Carregando prestadoras...</Text>
                </div>
              ) : filteredProviders.length > 0 ? (
                <div className="space-y-2 max-h-[300px] overflow-y-auto">
                  {filteredProviders.map((provider) => (
                    <div
                      key={provider.id}
                      className="flex items-center gap-3 p-3 border border-grey_200 rounded-lg hover:bg-grey_50 cursor-pointer"
                    >
                      <Radio value={provider.id} />
                      <div className="flex-1">
                        <Text strong>{provider.name}</Text>
                        <Text className="block text-sm text-grey_500">{provider.cnpj}</Text>
                      </div>
                      {provider.allowsDemandReceiving && (
                        <Tag color="green" showBorder>
                          Apta
                        </Tag>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex items-center justify-center flex-1">
                  <Empty
                    description={<Text className="text-grey_500">Nenhuma prestadora encontrada.</Text>}
                  />
                </div>
              )}
            </div>
          </Radio.Group>
        </Form.Item>

        <RegisterFormButtons onClickCancel={onClose} loading={isSubmitting} />
      </Form>
    </Modal>
  );
}
